/**
 * La marca de una empresa, para quien genera un informe (29-09-2026).
 *
 *  - Pregunta a la RPC `marca_informes` (la misma respuesta para cualquier rol:
 *    solo lo que la empresa enseña; el interruptor se mira allí).
 *  - Guarda la respuesta por EMPRESA unos minutos (el perito cambia de empresa y
 *    cada una trae la suya) y el logo por su RUTA (lleva la huella del fichero: una
 *    ruta es siempre la misma imagen). Los fallos NO se guardan.
 *  - Como mucho `ESPERA_MS`: un informe no se queda esperando a la red (en un
 *    invernadero con poca cobertura). Pasado ese tiempo, sin marca.
 *  - NUNCA lanza: sin red, sin la RPC o sin interruptor, `null`, y el informe sale
 *    como siempre (la marca de Ulture).
 *
 * `estadoMarca` (la fila de Mi cuenta, la página) NO baja el logo: solo dice si la
 * función está y qué hay guardado.
 *
 * No es un hook de react-query a propósito: se espera ANTES de construir la clave
 * de caché del export (si no, el primer export sale sin marca y se queda
 * guardado) y los tests de render no montan un QueryClient.
 */
import { supabase } from "@/integrations/supabase/client";
import i18n from "@/i18n/config";
import type { LogoMarca, MarcaInformes } from "./tipos";
import { componerLineas, type CamposCabecera } from "./lineas";

export const BUCKET_MARCA = "company-branding";
const VIGENCIA_MS = 5 * 60 * 1000;
/** Lo más que un informe espera a la marca (RPC + logo). */
export const ESPERA_MS = 3000;

export interface DatosMarca extends CamposCabecera {
  logo_path: string | null;
  logo_w: number | null;
  logo_h: number | null;
  color_principal: string;
  color_acento: string;
  estilo: string;
  nombre: string | null;
  version: string | null;
}

/** Lo que dice la RPC. `activo`: si la empresa tiene la función encendida. */
export interface EstadoMarca {
  activo: boolean;
  datos: DatosMarca | null;
}

const NADA: EstadoMarca = { activo: false, datos: null };
const estados = new Map<string, { hasta: number; p: Promise<EstadoMarca> }>();
const logos = new Map<string, Promise<LogoMarca | null>>();

/** Tras guardar en «Personalizar informes»: que el próximo informe pregunte otra vez. */
export function olvidarMarca(companyId?: string | null): void {
  if (companyId) estados.delete(companyId);
  else estados.clear();
}

/** `p`, o `valor` si tarda más de `ms` o falla. */
function conTope<T>(p: Promise<T>, ms: number, valor: T): Promise<T> {
  return new Promise((resolve) => {
    const t = setTimeout(() => resolve(valor), ms);
    p.then(
      (v) => {
        clearTimeout(t);
        resolve(v);
      },
      () => {
        clearTimeout(t);
        resolve(valor);
      },
    );
  });
}

const blobADataUrl = (blob: Blob) =>
  new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = () => reject(r.error);
    r.readAsDataURL(blob);
  });

/** La URL pública del logo (el correo y la vista previa la usan tal cual). */
export function urlLogoMarca(path: string): string {
  return supabase.storage.from(BUCKET_MARCA).getPublicUrl(path).data.publicUrl;
}

/** Una señal que se corta sola a los `ms` (lo cortado falla, y lo que falla no se guarda). */
function senalConTope(ms: number): AbortSignal {
  const c = new AbortController();
  setTimeout(() => c.abort(), ms);
  return c.signal;
}

async function bajarLogo(path: string, w: number, h: number): Promise<LogoMarca | null> {
  const res = await fetch(urlLogoMarca(path), { signal: senalConTope(ESPERA_MS) });
  if (!res.ok) throw new Error(`logo ${res.status}`);
  return { dataUrl: await blobADataUrl(await res.blob()), w, h };
}

/** El logo en data URL, una vez por ruta; un fallo no se guarda (se reintenta la próxima vez). */
function logoDe(path: string, w: number | null, h: number | null): Promise<LogoMarca | null> {
  if (!w || !h) return Promise.resolve(null);
  const hit = logos.get(path);
  if (hit) return hit;
  const p = bajarLogo(path, w, h);
  logos.set(path, p);
  p.catch(() => logos.delete(path));
  return p.catch(() => null);
}

async function leer(companyId: string): Promise<EstadoMarca> {
  const { data, error } = await supabase
    .rpc("marca_informes" as never, { p_company: companyId } as never)
    .abortSignal(senalConTope(ESPERA_MS));
  if (error) throw error;
  const r = (data ?? {}) as unknown as { activo?: boolean; marca?: DatosMarca | null };
  if (!r.activo) return NADA;
  return { activo: true, datos: r.marca ?? null };
}

/** Si la empresa tiene la función y lo que hay guardado (sin el logo). Nunca lanza. */
export function estadoMarca(companyId: string | null | undefined): Promise<EstadoMarca> {
  if (!companyId) return Promise.resolve(NADA);
  const hit = estados.get(companyId);
  if (hit && hit.hasta > Date.now()) return hit.p;
  const p = leer(companyId);
  const seguro = p.catch(() => NADA);
  estados.set(companyId, { hasta: Date.now() + VIGENCIA_MS, p: seguro });
  // Un fallo (sin red, RPC aún sin crear) no se queda guardado.
  p.catch(() => {
    if (estados.get(companyId)?.p === seguro) estados.delete(companyId);
  });
  return seguro;
}

async function construir(companyId: string): Promise<MarcaInformes | null> {
  const { datos } = await estadoMarca(companyId);
  if (!datos) return null;
  const logo = datos.logo_path ? await logoDe(datos.logo_path, datos.logo_w, datos.logo_h) : null;
  const et = {
    nif: i18n.t("marcaInformes.pdf.nif", { defaultValue: "NIF" }),
    tel: i18n.t("marcaInformes.pdf.tel", { defaultValue: "Tel." }),
  };
  const nombre = (datos.nombre ?? "").trim();
  return {
    nombre,
    logo,
    colorPrincipal: datos.color_principal,
    colorAcento: datos.color_acento,
    estilo: datos.estilo === "banda" ? "banda" : "blanca",
    lineas: componerLineas(datos, et, nombre),
    version: datos.version ?? "",
  };
}

/** La marca lista para un informe, o `null` (informe de siempre). Como mucho `esperaMs`. Nunca lanza. */
export async function cargarMarca(companyId: string | null | undefined, esperaMs = ESPERA_MS): Promise<MarcaInformes | null> {
  if (!companyId) return null;
  return conTope(construir(companyId), esperaMs, null);
}
